import React, { useEffect, useRef, useState } from "react";
import { keyframes } from '@emotion/react';
import axios from "axios";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Container,
  Paper,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from "@mui/material";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import SendIcon from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";
import Footer from "../components/Footer/Footer";
import Header from "../components/Header/Header";

const apiEndpoint = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api/v1/openAi/paris/chat`
  : "/api/v1/openAi/paris/chat";

const sendTranscriptEndpoint = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api/v1/openAi/paris/send-transcript`
  : "/api/v1/openAi/paris/send-transcript";

const ACCENT_COLOR = "#9CA3AF";
const LEAVE_WARNING_TEXT =
  "Your Ask PARIS chat will be lost if you refresh or leave this page.";
const RELOAD_NOTICE_KEY = "ask_paris_reload_notice";

const INITIAL_GREETING = {
  role: "assistant",
  content:
    `Hello, I’m the Production Assistant for Rapid Ideas and Support, you may call me PARIS.
I help associations use video to engage their members and industry.

What is the name of your organization, and what would you like to use video for?`,
};

const bubbleStyle = {
  maxWidth: { xs: "100%", md: "75%" },
  padding: "14px 16px",
  borderRadius: "18px",
  boxShadow: "0 8px 24px rgba(15,23,42,0.08)",
  wordBreak: "break-word",
  whiteSpace: "pre-wrap",
};

function AskParis() {
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([INITIAL_GREETING]);
  const [sessionId, setSessionId] = useState(null);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendSuccess, setSendSuccess] = useState("");
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [isEmbedded, setIsEmbedded] = useState(() => (typeof window !== 'undefined' && window.self !== window.top));
  const [embedAllowed, setEmbedAllowed] = useState(true);
  const [embedParentHost, setEmbedParentHost] = useState(null);
    const isValidEmail = (value) => {
      if (!value) return false;
      const emailRegex = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+$/;
      return emailRegex.test(value);
    };

    const handleSendTranscript = async () => {
      setEmailError("");
      setSendSuccess("");
      if (!isValidEmail(email)) {
        setEmailError("Please enter a valid email address.");
        return;
      }

      if (!sessionId) {
        setEmailError("Please start a chat before sending the conversation.");
        return;
      }

      setSendingEmail(true);
      try {
        const resp = await axios.post(sendTranscriptEndpoint, { sessionId, email });
        if (resp?.data?.success) {
          setSendSuccess("Transcript sent — check your inbox shortly.");
          setEmail("");
          setEmailDialogOpen(false);
        } else {
          setEmailError(resp?.data?.error || "Failed to send transcript.");
        }
      } catch (err) {
        console.error('Error sending transcript:', err);
        setEmailError(err?.response?.data?.error || 'Failed to send transcript.');
      } finally {
        setSendingEmail(false);
      }
    };

    const openEmailDialog = () => {
      setEmailDialogOpen(true);
      setEmailError("");
      setSendSuccess("");
    };

    const closeEmailDialog = () => {
      setEmailDialogOpen(false);
    };
  const [isLoading, setIsLoading] = useState(false);
  const [showLeaveWarning, setShowLeaveWarning] = useState(false);
  const [hideChrome, setHideChrome] = useState(false);
  // Show the main site header only when served from the snapshotvideos.com origin
  // and not when the page is embedded in an iframe on another site.
  const showMainHeader = !isEmbedded && typeof window !== 'undefined' && window.location && window.location.origin === 'https://snapshotvideos.com';
  const endRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const chatWrapperRef = useRef(null);
  const headerRef = useRef(null);
  const [minimized, setMinimized] = useState(false);
  const touchStartYRef = useRef(null);
  const minimizedRef = useRef(minimized);
  const hasActiveConversation = messages.length > 1;
  // Previously we disabled the pinned header when embedded on associationstudios.com
  // for mobile. That prevented the compact header from appearing inside iframes.
  // Keep pining enabled so the compact header shows when embedded.
  const disablePin = false;

  const quickPrompts = [
    "Engage members",
    "Conferences & Events",
    "Advocacy",
  ];

  // Insert starter options into the messages list on mount (idempotent).
  // Use the functional updater so repeated mounts (React Strict Mode)
  // won't add duplicates.
  useEffect(() => {
    setMessages((prev) => {
      try {
        const hasUser = Array.isArray(prev) && prev.some((m) => m.role === 'user');
        const hasOptions = Array.isArray(prev) && prev.some((m) => m.role === 'options');
        if (!hasUser && !hasOptions) {
          return [...prev, { role: 'options', options: quickPrompts }];
        }
      } catch (e) {}
      return prev;
    });
    // run only once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // On mobile devices start with the compact (minimized) header so the
  // chat appears open immediately. This applies whether embedded or not.
  useEffect(() => {
    if (isMobile) {
      setMinimized(true);
    }
  }, [isMobile]);

  const scrollToBottom = () => {
    const container = messagesContainerRef.current;
    if (container) {
      // Use direct scrollTop update inside RAF to avoid causing the window to scroll.
      try {
        requestAnimationFrame(() => {
          container.scrollTop = container.scrollHeight - container.clientHeight;
        });
        return;
      } catch (e) {
        // fall through to element.scrollIntoView
      }
    }
    if (endRef.current) {
      try {
        endRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      } catch (e) {}
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Pin header/input when chat reaches top of viewport (single effect, stable refs)
  // Keep ref in sync to avoid stale closures in scroll handler
  useEffect(() => { minimizedRef.current = minimized; }, [minimized]);

  // Robust minimize-on-scroll: prefer messages container scroll when available,
  // otherwise fall back to window scrolling. Add debounce and hysteresis so
  // small layout changes (mobile address bar hide/show) don't toggle header.
  useEffect(() => {
    // On mobile we want the compact header fixed and never auto-hidden by
    // page scrolling. Force minimized and skip scroll handlers on mobile.
    if (isMobile) { setMinimized(true); return; }
    if (disablePin) { setMinimized(false); return; }

    const MC_ENTER = 24; // messages container scrollTop to minimize
    const MC_EXIT = 8;
    const WIN_ENTER = 100; // window.pageYOffset to minimize
    const WIN_EXIT = 40;
    const MIN_TOGGLE_MS = 300;

    let lastToggle = 0;

    const maybeToggle = (shouldMinimize) => {
      const now = Date.now();
      if (now - lastToggle < MIN_TOGGLE_MS) return;
      if (shouldMinimize !== minimizedRef.current) {
        lastToggle = now;
        setMinimized(shouldMinimize);
      }
    };

    const mc = messagesContainerRef.current;
    const mcScrollable = mc && mc.scrollHeight && mc.clientHeight && mc.scrollHeight > mc.clientHeight;
    if (mcScrollable && typeof mc.addEventListener === 'function') {
      const onMcScroll = () => {
        try {
          const st = mc.scrollTop || 0;
          if (!minimizedRef.current && st > MC_ENTER) maybeToggle(true);
          else if (minimizedRef.current && st <= MC_EXIT) maybeToggle(false);
        } catch (e) {}
      };
      mc.addEventListener('scroll', onMcScroll, { passive: true });
      // run once to initialize
      onMcScroll();
      return () => mc.removeEventListener('scroll', onMcScroll);
    }

    // fallback to window scroll
    let ticking = false;
    const onWinScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        try {
          const y = window.scrollY || window.pageYOffset || 0;
          if (!minimizedRef.current && y > WIN_ENTER) maybeToggle(true);
          else if (minimizedRef.current && y < WIN_EXIT) maybeToggle(false);
        } catch (e) {}
        ticking = false;
      });
    };

    window.addEventListener('scroll', onWinScroll, { passive: true });
    window.addEventListener('touchmove', onWinScroll, { passive: true });
    onWinScroll();
    return () => {
      window.removeEventListener('scroll', onWinScroll);
      window.removeEventListener('touchmove', onWinScroll);
    };
  }, [disablePin, isMobile]);

  useEffect(() => {
    if (sessionStorage.getItem(RELOAD_NOTICE_KEY)) {
      setShowLeaveWarning(true);
      sessionStorage.removeItem(RELOAD_NOTICE_KEY);
    }
  }, []);

  useEffect(() => {
    try {
      const inIframe = window && window.self !== window.top;
      const ref = (document && document.referrer) || "";
      let parentHost = null;
      try {
        parentHost = window.parent && window.parent.location && window.parent.location.hostname;
      } catch (e) {
        parentHost = null;
      }

      if (inIframe && (ref.includes("associationstudios.com") || (parentHost && parentHost.includes("associationstudios.com")))) {
        setHideChrome(true);
      }
    } catch (e) {}
  }, []);

  // Allow embedding from any host. Remember parent/ref for layout tweaks.
  useEffect(() => {
    try {
      const inIframe = window && window.self !== window.top;
      if (!inIframe) return;
      setIsEmbedded(true);

      const ref = (document && document.referrer) || "";
      let parentHost = null;
      try {
        parentHost = window.parent && window.parent.location && window.parent.location.hostname;
      } catch (e) {
        parentHost = null;
      }

      let refHost = null;
      try {
        if (ref) refHost = new URL(ref).hostname;
      } catch (e) {
        refHost = null;
      }

      // remember parent host for domain-specific layout tweaks
      const parentOrRef = parentHost || refHost || null;
      setEmbedParentHost(parentOrRef);
      // allow embedding on any host
      setEmbedAllowed(true);

      // If embedded on associationstudios.com, hide document scrollbars
      if (parentOrRef && parentOrRef.includes('associationstudios.com')) {
        try {
          const prevHtmlOverflow = document.documentElement.style.overflow;
          const prevBodyOverflow = document.body.style.overflow;
          const prevMsOverflow = document.documentElement.style.msOverflowStyle;
          const prevScrollbarWidth = document.documentElement.style.scrollbarWidth;

          document.documentElement.style.scrollbarWidth = 'none';

          return () => {
            try {
              document.documentElement.style.overflow = prevHtmlOverflow || '';
              document.body.style.overflow = prevBodyOverflow || '';
              document.documentElement.style.msOverflowStyle = prevMsOverflow || '';
              document.documentElement.style.scrollbarWidth = prevScrollbarWidth || '';
            } catch (e) {}
          };
        } catch (e) {}
      }
    } catch (e) {
      setEmbedAllowed(true);
      setIsEmbedded(true);
    }
  }, []);

  useEffect(() => {
    if (!hasActiveConversation) return;

    const handleBeforeUnload = (event) => {
      // show in-chat warning immediately so user sees it if they cancel
      try {
        setShowLeaveWarning(true);
      } catch (e) {}
      // mark intent — will be cleared on actual unload (pagehide)
      sessionStorage.setItem(RELOAD_NOTICE_KEY, "true");
      event.preventDefault();
      event.returnValue = LEAVE_WARNING_TEXT;
      return LEAVE_WARNING_TEXT;
    };

    const handlePageHide = () => {
      try {
        sessionStorage.removeItem(RELOAD_NOTICE_KEY);
      } catch (e) {}
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("pagehide", handlePageHide);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, [hasActiveConversation]);

  useEffect(() => {
    if (!hasActiveConversation) return;

    const handleDocumentClick = (event) => {
      const target = event.target;
      const anchor = target && typeof target.closest === "function" ? target.closest("a") : null;
      if (!anchor) return;
      if (anchor.target === "_blank" || anchor.hasAttribute("download")) return;
      const href = anchor.getAttribute("href") || "";
      if (!href || href.startsWith("#")) return;

      // show in-chat warning immediately before showing the confirm dialog
      try {
        setShowLeaveWarning(true);
      } catch (e) {}
      // show in-chat warning immediately before showing the confirm dialog
      try {
        setShowLeaveWarning(true);
      } catch (e) {}
      const confirmLeave = window.confirm(LEAVE_WARNING_TEXT);
      if (!confirmLeave) {
        event.preventDefault();
        event.stopPropagation();
      } else {
        // user confirmed navigation — ensure the reload notice won't persist after navigation
        try {
          sessionStorage.removeItem(RELOAD_NOTICE_KEY);
        } catch (e) {}
      }
    };

    document.addEventListener("click", handleDocumentClick, true);
    return () => {
      document.removeEventListener("click", handleDocumentClick, true);
    };
  }, [hasActiveConversation]);

  const handleSend = async (prefill) => {
    const base = typeof prefill === "string" ? prefill : input;
    const trimmed = base.trim();
    if (!trimmed || isLoading) return;

    const userMessage = { role: "user", content: trimmed };
    // determine if this is the user's first message in the conversation
    const isFirstUserMessage = Array.isArray(messages) && messages.filter(m => m.role === 'user').length === 0;
    setMessages((prev) => {
      try {
        const withoutOptions = Array.isArray(prev) ? prev.filter(m => m.role !== 'options') : prev;
        return [...withoutOptions, userMessage];
      } catch (e) {
        return [...prev, userMessage];
      }
    });
    // ensure the user's message is visible immediately after sending
    try {
      requestAnimationFrame(() => {
        scrollToBottom();
        if (isFirstUserMessage) {
          // force minimize header and scroll the page so the chat is visible under the fixed header
          try {
            setMinimized(true);
          } catch (e) {}
          try {
            const topRect = chatWrapperRef?.current?.getBoundingClientRect();
            const headerH = headerRef?.current ? headerRef.current.getBoundingClientRect().height : 80;
            if (topRect) {
              const target = window.pageYOffset + topRect.top - headerH - 8;
              window.scrollTo({ top: target, behavior: 'smooth' });
            }
          } catch (e) {}
        }
      });
    } catch (e) {
      setTimeout(() => {
        scrollToBottom();
        if (isFirstUserMessage) {
          try { setMinimized(true); } catch (e) {}
          try {
            const topRect = chatWrapperRef?.current?.getBoundingClientRect();
            const headerH = headerRef?.current ? headerRef.current.getBoundingClientRect().height : 80;
            if (topRect) {
              const target = window.pageYOffset + topRect.top - headerH - 8;
              window.scrollTo({ top: target, behavior: 'smooth' });
            }
          } catch (e) {}
        }
      }, 50);
    }
    // Clear the input immediately so selected quick-prompts do not remain
    // visible in the input field on mobile devices.
    try { setInput(""); } catch (e) {}
    setIsLoading(true);

    try {
      const response = await axios.post(apiEndpoint, {
        sessionId,
        message: trimmed,
        origin: "ask-paris-web",
      });
      console.log("PARIS response:", response.data);
      if (response?.data?.sessionId) {
        setSessionId(response.data.sessionId);
      }

      if (Array.isArray(response?.data?.messages)) {
        setMessages([INITIAL_GREETING, ...response.data.messages]);
      } else {
        const replyContent =
          response?.data?.message || "PARIS did not send a response."; 
        const assistantMessage = { role: "assistant", content: replyContent };
        setMessages((prev) => [...prev, assistantMessage]);
      }
    } catch (error) {
      console.error("Error calling PARIS chat:", error);
      const errorMessage = "Sorry, something went wrong. Please try again.";
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: errorMessage },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const handleQuickPrompt = (prompt) => {
    // Do not prefill the input field when using a quick prompt; send directly
    // so the selected option does not appear in the input on mobile.
    handleSend(prompt);
  };

  const renderCallToAction = (content) => {
    const trigger = /schedule a call/i.test(content);
    if (!trigger) return null;

    const handleBookConsultation = () => {
      const target = "https://calendly.com/jimwacksman/snapshot-videos-discussion";
      window.open(target, "_blank", "noopener,noreferrer");
    };

    return (
      <Button
        variant="contained"
        sx={{
          backgroundColor: ACCENT_COLOR,
          mt: 1,
          px: 2,
          "&:hover": { backgroundColor: "#a30000" },
        }}
        size="small"
        onClick={handleBookConsultation}
      >
        schedule a call
      </Button>
    );
  };

  const renderLinkButtons = (content) => {
    if (!content) return null;
    const snapshotVideoUrl = 'https://snapshotvideos.com';
    const subscriptionUrl = 'https://associationstudios.com/subscriptions';
    const calendlyUrl = 'https://associationstudios.com/meet-with-jim';
    const buttons = [];

    const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const matchUrl = (text, url) => {
      if (!text || !url) return false;
      const normalized = url.replace(/\/+$|#$/g, '');
      const pattern = new RegExp(escapeRegExp(normalized) + '(?:[\\/#]|$)', 'i');
      return pattern.test(text) || new RegExp(escapeRegExp(url.replace(/\/+$|#$/g, '')), 'i').test(text) || new RegExp(escapeRegExp(normalized), 'i').test(text);
    };

    if (matchUrl(content, subscriptionUrl) || /\bsubscription\b/i.test(content)) {
      buttons.push(
        <Button
          key="subscription-btn"
          variant="contained"
          sx={{ backgroundColor: ACCENT_COLOR, mt: 1, px: 2, "&:hover": { backgroundColor: "#a30000" } }}
          size="small"
          onClick={() => window.open(subscriptionUrl, '_blank', 'noopener,noreferrer')}
        >
          subscription
        </Button>
      );
    }

    if (content.includes(snapshotVideoUrl)) {
      buttons.push(
        <Button
          key="snapshot-btn"
          variant="contained"
          sx={{ backgroundColor: ACCENT_COLOR, mt: 1, px: 2, "&:hover": { backgroundColor: "#a30000" } }}
          size="small"
          onClick={() => window.open(snapshotVideoUrl, '_blank', 'noopener,noreferrer')}
        >
          Snapshot Videos
        </Button>
      );
    }

    if (matchUrl(content, calendlyUrl)) {
      buttons.push(
        <Button
          key="calendly-btn"
          variant="contained"
          sx={{ backgroundColor: ACCENT_COLOR, mt: 1, px: 2, "&:hover": { backgroundColor: "#a30000" } }}
          size="small"
          onClick={() => window.open(calendlyUrl, '_blank', 'noopener,noreferrer')}
        >
          schedule a call
        </Button>
      );
    }

    if (buttons.length === 0) return null;
    return (
      <Box mt={1} display="flex" gap={1}>
        {buttons}
      </Box>
    );
  };
  // Allow rendering even when embedded on other hosts; embedding is permitted.

  return (
    <>
      {showMainHeader && <Header />}
      <Box
        sx={(theme) => ({
          minHeight: "100vh",
          backgroundColor: "#ffffff",
          pt: hideChrome ? 2 : (isEmbedded ? (isMobile ? 6 : 10) : { xs: 14, md: 14 }),
          // hide scrollbars for full page when embedded on associationstudios.com
          ...(embedParentHost && embedParentHost.includes('associationstudios.com') ? {
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          } : {}),
        })}
      >
        <Box>
          <Box
            ref={chatWrapperRef}
            sx={{
              borderRadius: 4,
              background: "#ffffff",
              color: "#000000",
            }}
          >

            {/* Pinned minimal header (always rendered) — animate visibility for smooth transition */}
            <Box
              ref={headerRef}
              sx={(theme) => ({
                position: 'fixed',
                // add extra top offset when embedded on associationstudios.com
                // top: isEmbedded ? (isMobile ? 16 : 50) : 83,
                top: isEmbedded ? 11 : 83,
                left: 0,
                width: '100%',
                zIndex: 1200,
                display: disablePin ? 'none' : 'flex',
                alignItems: 'center',
                gap: 2,
                p: 1,
                borderRadius: 2,
                bgcolor: '#ffffff',
                boxShadow: '0 8px 24px rgba(15,23,42,0.08)',
                // animate in/out
                transform: minimized ? 'translateY(0)' : 'translateY(-10px)',
                opacity: minimized ? 1 : 0,
                transition: 'transform 220ms ease, opacity 220ms ease',
                pointerEvents: minimized ? 'auto' : 'none',
              })}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', px: { xs: 2, md: 8 } }}>
                <Avatar src="/images/other/PARIS Headshot.png" sx={{ width: 44, height: 44 }} />
                <Typography sx={{ fontWeight: 700, ml: 1, fontSize: { xs: '0.95rem', md: '1rem' } }}>Ask PARIS</Typography>
              </Box>
            </Box>
            {/* Starter options will be inserted into the message list as a special message */}

            {/* Chat header (hidden when minimized so compact fixed header is sole UI)
                On mobile we always hide the large header and show only the
                compact pinned header so the chat appears open immediately. */}
            <Box
                sx={(theme) => ({
                  display: (isMobile || minimized) ? 'none' : undefined,
                  mx: { xs: 0, md: '35px' },
                  px: { xs: 2, sm: 3, md: 8 },
                  py: { xs: 5, md: 8 },
                  position: "relative",
                  overflow: "hidden",
                  marginBottom: '0px',
                  transition: 'padding 220ms ease, margin-bottom 220ms ease',
                  // if embedded on associationstudios, add top padding so chat header doesn't sit under host header
                  pt: (embedParentHost && embedParentHost.includes('associationstudios.com')) ? { xs: 8, md: 10 } : undefined,
                })}
              >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  alignItems: "center",
                  gap: { xs: 4, md: 6 },
                }}
              >
                
                <Box sx={{ position: "relative" }}>
                  <Avatar
                    src="/images/other/PARIS Headshot.png"
                    sx={(theme) => ({
                      width: minimized ? 44 : { xs: 100, md: 180 },
                      height: minimized ? 44 : { xs: 100, md: 180 },
                      transition: 'width 220ms ease, height 220ms ease',
                    })}
                  />

                  <Box
                    component="img"
                    src="/images/cross-copy.png" // dummy image
                    alt="decorative star"
                    sx={{
                      position: "absolute",
                      top: -10,
                      left: -30,
                      width: 60,
                    }}
                  />
                </Box>

                {/* Text Section (will collapse when pinned) */}
                <Box sx={{ maxWidth: 900, transition: 'opacity 220ms ease, max-height 220ms ease', opacity: minimized ? 0 : 1, maxHeight: minimized ? 0 : '1000px', overflow: 'hidden' }}>
                  <Typography
                    variant="h3"
                    fontWeight={500}
                    sx={(theme) => ({ fontSize: minimized ? '1.0rem' : { xs: '1.2rem', md: '2.5rem' }, transition: 'font-size 220ms ease' })}
                  >
                    Have questions about{" "}
                    <Box component="span" fontWeight={800}>
                      video production
                    </Box>{" "}
                    and{" "}
                    <Box component="span" fontWeight={800}>
                      member engagement?
                    </Box>{" "}
                    <Box
                      component="span"
                      sx={{ color: "#D10000", fontWeight: 800, textDecoration: "underline" }}
                    >
                      Let’s talk.
                    </Box>
                  </Typography>

                  <Typography
                    variant="h4"
                      sx={{ mt: minimized ? 0 : 2, color: "#4B5563", fontWeight: 400, transition: 'margin-top 220ms ease', fontSize: { xs: '0.95rem', md: '1.1rem' } }}
                  >
                    Ask PARIS for practical ideas on using video to better
                    engage your members.
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Messages Section */}
            <Box px={{ xs: 2, md: 8 }} py={{xs: 8, md: 10}} height={'85vh'} backgroundColor="#F3F4F6" position="relative">
              {!minimized && <Box
                component="img"
                src="/images/dots.png" // dummy image
                alt="decorative dots"
                sx={{
                  position: "absolute",
                  top: -45,
                  right: 40,
                  width: 120,
                  opacity: 0.5,
                }}
              />}
              {showLeaveWarning && (
                <Box
                  sx={{
                    backgroundColor: "#FEF3C7",
                    border: "1px solid #FCD34D",
                    color: "#92400E",
                    borderRadius: 2,
                    p: 1.5,
                    mb: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                  role="status"
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      fontSize: { xs: "0.85rem", md: "0.95rem" },
                    }}
                  >
                    {LEAVE_WARNING_TEXT}
                  </Typography>
                  <IconButton
                    size="small"
                    aria-label="Dismiss chat warning"
                    onClick={() => {
                      sessionStorage.removeItem(RELOAD_NOTICE_KEY);
                      setShowLeaveWarning(false);
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
              <Box
                sx={{
                  // border: "1px solid #f3d1d1",
                  borderRadius: 3,
                  // p: { xs: 2, md: 3 },
                  backgroundColor: "#F3F4F6",
                  minHeight: isMobile ? "65vh" : isTablet ? "70vh" : "68vh",
                  maxHeight: "70vh",
                  // paddingTop: minimized ? '72px' : undefined,
                  // ensure the message list has enough bottom padding so
                  // quick-prompt buttons aren't overlapped by the fixed
                  // input on mobile devices
                  paddingBottom: { xs: '160px', md: '120px' },
                  overflowY: "auto",
                  // slimmer scrollbar for message list
                  '&::-webkit-scrollbar': {
                    width: '8px',
                    height: '8px'
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'transparent'
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#c4c4c4',
                    borderRadius: 8
                  },
                  scrollbarWidth: 'thin',
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
                ref={messagesContainerRef}
              >
                {messages.map((msg, index) => {
                  // Special-render an options message (selectable suggestions)
                  if (msg.role === 'options' && Array.isArray(msg.options)) {
                    // Render options as a user's bubble (right-aligned) so it appears
                    // that the user is choosing a prepared message.
                    return (
                      <Box key={index} display="flex" justifyContent="flex-end">
                        <Box sx={{ ...bubbleStyle, padding: isMobile ? '10px 12px' : bubbleStyle.padding, background: `linear-gradient(135deg, ${ACCENT_COLOR}, #bfc8cc)`, border: 'none', color: '#000000' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, fontSize: isMobile ? '0.7rem' : undefined }}>Choose a question</Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5, justifyContent: 'flex-end' }}>
                            {msg.options.map((q) => (
                              <Button
                                key={q}
                                size="small"
                                variant="contained"
                                onClick={() => {
                                  try { setMessages(prev => prev.filter(m => m.role !== 'options')); } catch (e) {}
                                  handleQuickPrompt(q);
                                }}
                                sx={{ textTransform: 'none', backgroundColor: '#ffffff', color: '#111827', '&:hover': { backgroundColor: '#f3f4f6' }, touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
                              >
                                {q}
                              </Button>
                            ))}
                          </Box>
                        </Box>
                      </Box>
                    );
                  }
                  const isUser = msg.role === "user";
                  return (
                    <Box
                      key={index}
                      display="flex"
                      justifyContent={isUser ? "flex-end" : "flex-start"}
                    >
                      <Box
                        sx={{
                          ...bubbleStyle,
                          padding: isMobile ? "10px 12px" : bubbleStyle.padding,
                          background: isUser
                            ? `linear-gradient(135deg, ${ACCENT_COLOR}, #bfc8cc)`
                            : "linear-gradient(135deg, #ffffff, #f8fafc)",
                          border: isUser ? "none" : "1px solid #e5e7eb",
                          color: "#000000",
                          textAlign: "left",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            mb: 0.5,
                            fontSize: isMobile ? "0.7rem" : undefined,
                          }}
                        >
                          {isUser ? "You" : "PARIS"}
                        </Typography>
                        {(() => {
                          const raw = typeof msg.content === 'string' ? msg.content : JSON.stringify(msg.content, null, 2);
                          // Convert escaped newline sequences into real newlines for rendering
                          // Handle both double-escaped and single-escaped sequences
                          let contentForMarkdown = raw.replace(/\\\\n/g, '\n')
                            .replace(/\\n/g, '\n')
                            .replace(/\\\\r/g, '\r')
                            .replace(/\\r/g, '\r')
                            .replace(/\\\\t/g, '\t')
                            .replace(/\\t/g, '\t');

                          // Escape leading list markers (e.g. "- ") so they are
                          // displayed as literal hyphens instead of being parsed
                          // as Markdown lists. This preserves the original
                          // formatting the backend returned.
                          contentForMarkdown = contentForMarkdown.replace(/^(\s*)([-*+])\s+/mg, '$1\\$2 ');
                          // Prevent numbered markers like "1)" or "1." being parsed
                          // as ordered lists while keeping the visual text the same
                          // by inserting a zero-width space between the number and
                          // the following ")" or ".". This preserves "1)"/"1." visually.
                          contentForMarkdown = contentForMarkdown.replace(/^(\s*)(\d+)([\.)])\s+/mg, '$1$2\u200B$3 ');

                          // Preserve lettered markers like "(a)", "(b)" by inserting
                          // a zero-width space inside the parentheses so they are
                          // displayed literally and not interpreted by any markdown
                          // processors. This keeps the visual text identical.
                          contentForMarkdown = contentForMarkdown.replace(/^(\s*)\(([a-zA-Z])\)(\s+)/mg, '$1($2\u200B)$3');

                          // If content was an object, render as a JSON code block for readability
                          if (typeof msg.content !== 'string') {
                            contentForMarkdown = '```json\n' + raw + '\n```';
                          }

                          // Preserve leading indentation: replace leading spaces on each
                          // line with non-breaking spaces so Markdown rendering keeps
                          // the visual indentation without converting it to list or
                          // other structural markdown.
                          if (typeof contentForMarkdown === 'string') {
                            contentForMarkdown = contentForMarkdown.split('\n').map(line => {
                              const m = line.match(/^(\s+)/);
                              if (!m) return line;
                              return '\u00A0'.repeat(m[1].length) + line.slice(m[1].length);
                            }).join('\n');
                          }

                          return (
                            <Box sx={{ fontSize: isMobile ? '0.85rem' : '0.95rem', lineHeight: 1.5, '& a': { color: '#D10000' } }}>
                              <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                components={{
                                  a: ({ node, ...props }) => (
                                    <a {...props} target="_blank" rel="noopener noreferrer" />
                                  ),
                                  // Inline code and code blocks
                                  code: ({ inline, className, children, ...props }) => {
                                    if (inline) {
                                      return (
                                        <Box component="code" sx={{ backgroundColor: '#F3F4F6', px: '6px', py: '2px', borderRadius: 1, fontSize: isMobile ? '0.78rem' : '0.85rem' }}>
                                          {children}
                                        </Box>
                                      );
                                    }
                                    return (
                                      <Box component="pre" sx={{ backgroundColor: '#0f172a0a', p: 1.5, borderRadius: 1.5, overflowX: 'auto', fontSize: isMobile ? '0.8rem' : '0.85rem', lineHeight: 1.4 }}>
                                        <Box component="code">{children}</Box>
                                      </Box>
                                    );
                                  },
                                  // Paragraphs: tighten spacing and font
                                  p: ({ node, ...props }) => (
                                    <Box component="p" sx={{ margin: '0 0 8px 0', fontSize: isMobile ? '0.85rem' : '0.95rem', lineHeight: 1.5 }} {...props} />
                                  ),
                                  // Lists and list items: normalize font size
                                  ul: ({ node, ...props }) => (
                                    <Box component="ul" sx={{ margin: '0 0 8px 18px', padding: 0, fontSize: isMobile ? '0.85rem' : '0.95rem', lineHeight: 1.5 }} {...props} />
                                  ),
                                  ol: ({ node, ...props }) => (
                                    <Box component="ol" sx={{ margin: '0 0 8px 18px', padding: 0, fontSize: isMobile ? '0.85rem' : '0.95rem', lineHeight: 1.5 }} {...props} />
                                  ),
                                  li: ({ node, ...props }) => (
                                    <Box component="li" sx={{ margin: '0 0 6px 0', fontSize: isMobile ? '0.85rem' : '0.95rem', lineHeight: 1.5 }} {...props} />
                                  ),
                                  strong: ({ node, ...props }) => (
                                    <Box component="strong" sx={{ fontWeight: 700 }} {...props} />
                                  ),
                                  em: ({ node, ...props }) => (
                                    <Box component="em" sx={{ fontStyle: 'italic' }} {...props} />
                                  ),
                                  h1: ({ node, ...props }) => (
                                    <Box component="h1" sx={{ fontSize: isMobile ? '1rem' : '1.05rem', margin: '0 0 8px 0', fontWeight: 700 }} {...props} />
                                  ),
                                  h2: ({ node, ...props }) => (
                                    <Box component="h2" sx={{ fontSize: isMobile ? '0.98rem' : '1.02rem', margin: '0 0 8px 0', fontWeight: 700 }} {...props} />
                                  ),
                                  blockquote: ({ node, ...props }) => (
                                    <Box component="blockquote" sx={{ borderLeft: '3px solid #e5e7eb', pl: 2, color: '#374151', margin: '0 0 8px 0' }} {...props} />
                                  )
                                }}
                              >
                                {contentForMarkdown}
                              </ReactMarkdown>
                              {!isUser && renderLinkButtons(contentForMarkdown)}
                            </Box>
                          );
                        })()}
                      </Box>
                    </Box>
                  );
                })}
                {isLoading && (
                  <Box display="flex" justifyContent="flex-start">
                    <Box
                      sx={{
                        ...bubbleStyle,
                        background: "linear-gradient(135deg, #ffffff, #f8fafc)",
                        border: "1px dashed #d1d5db",
                        fontStyle: "italic",
                        color: "#000000",
                      }}
                    >
                      <ThreeDots />
                    </Box>
                  </Box>
                )}
                <div ref={endRef} />
              </Box>

              <Box
                mt={3}
                display="flex"
                // gap={1.5}
                alignItems="stretch"
                // flexDirection={isTablet ? "column" : "row"}
                sx={{ display: 'none', px: { xs: 2, md: 8 } }}
                backgroundColor="#F3F4F6"
              >
                <Box sx={{ width: '100%', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'stretch' }}>
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={1}
                      maxRows={4}
                      // placeholder="Ask PARIS for video ideas, production guidance, and marketing strategy."
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={handleKeyDown}
                      sx={{
                        bgcolor: "#ffffff",
                        borderRadius: 2,
                        border: "1px solid #e5e7eb",
                        "& .MuiInputBase-input": { color: "#000000" },
                        "& .MuiInputBase-input::placeholder": { color: "#000000", opacity: 1 },
                        "& .MuiOutlinedInput-root": {
                          padding: "0px 10px 0px 10px",
                        },
                        "& fieldset": { border: "none" },
                      }}
                    />
                  </Box>
                  <Button
                    variant="contained"
                    aria-label="send message"
                    sx={{
                      backgroundColor: "#D10000",
                      px: 3,
                      maxHeight: 103,
                      width: { xs: '100%', md: '103px' },
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 400,
                      color: "#ffffff",
                      minWidth: { md: '103px' },
                      mt: { xs: 1, md: 0 },
                      "&:hover": { backgroundColor: "#6b7280" },
                    }}
                    onClick={() => handleSend()}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <>SEND »</>
                    )}
                  </Button>

                </Box>
                {/* helper text removed from here — now shown below fixed input */}
              </Box>

              

              {/* Always-fixed bottom input (visible on page load) */}
              <Box sx={{ position: 'fixed', bottom: 1, left: 0, right: 0, zIndex: 1300, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'auto', backgroundColor: '#F3F4F6' }}>
                <Box sx={{ width: { xs: 'calc(100% - 32px)', md: 'calc(100% - 32px)' }, p: 1, display: 'flex', gap: 1, alignItems: 'center', flexDirection: 'row', flexWrap: 'nowrap' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={1}
                      maxRows={4}
                      // placeholder="Ask PARIS for video ideas, production guidance, and marketing strategy."
                      placeholder="What would you like help with today?"
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={handleKeyDown}
                      sx={{
                          bgcolor: "#ffffff",
                          borderRadius: 2,
                          border: "1px solid #e5e7eb",
                          "& .MuiInputBase-input": { color: "#000000" },
                          "& .MuiInputBase-input::placeholder": { color: "#9e9e9eff", opacity: 1, fontSize: { xs: '0.8rem', md: '0.95rem' } },
                          "& .MuiOutlinedInput-root": {
                            padding: "0px 10px",
                          },
                          "& fieldset": { border: "none" },
                        }}
                    />
                  </Box>
                  <Button
                    variant="contained"
                    aria-label="send message"
                    sx={{
                      backgroundColor: "#D10000",
                      px: { xs: 1, md: 2 },
                      height: 48,
                      minHeight: 48,
                      width: { xs: 48, md: 'auto' },
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 400,
                      color: "#ffffff",
                      minWidth: { xs: 48 },
                      mt: { xs: 0, md: 0 },
                      borderRadius: 2,
                      "&:hover": { backgroundColor: "#b00000" },
                    }}
                    onClick={() => handleSend()}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      isMobile ? <SendIcon sx={{ fontSize: 20 }} /> : <>SEND »</>
                    )}
                  </Button>
                </Box>
                <Box sx={{ width: { xs: 'calc(100% - 32px)', md: 'calc(100% - 32px)' }, px: 1, mt: { xs: 0.5, md: 0 }, display: 'flex', justifyContent: 'flex-start' }}>
                  <Typography variant="body2" sx={{ color: '#6B7280', fontSize: { xs: '0.72rem', md: '0.9rem' }, textAlign: 'center' }}>
                    👉 When you’re finished, enter your email and we’ll send your video plan and next steps.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Email Dialog (opened from helper links) */}
      <Dialog open={emailDialogOpen} onClose={closeEmailDialog} fullWidth maxWidth="md">
        <DialogTitle sx={{ m: 0, p: 2, fontSize: { xs: "1rem", md: "1.2rem" }, }} >
          Send conversation
          <IconButton
            aria-label="close"
            onClick={closeEmailDialog}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Box
            sx={{
              backgroundColor: "#fff",
              py: { xs: 3, md: 5 },
              my: '10px',
              px: { xs: 0, md: 3 },
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 30,
                left: { xs: 20, md: 120 },
                width: 60,
                opacity: 0.4,
              }}
            />

            <Container maxWidth="md">
              <Box textAlign="center" mb={3}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 500,
                    color: "#374151",
                    fontSize: { xs: "1rem", md: "1.2rem" },
                  }}
                >
                  When you're finished, add your email to receive a copy of the
                  conversation.
                </Typography>
              </Box>

              <Box
                sx={{
                  position: "relative", // important
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  gap: { xs: '10px' , md: 0 },
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box
                  component="img"
                  src="/images/cross-copy.png" // dummy image
                  alt="decorative pattern"
                  sx={{
                    position: "absolute",
                    top: -44,
                    left: { xs: '-23px', md: -57 },
                    width: 79,
                    opacity: 0.4,
                    pointerEvents: "none",
                    maxHeight: '130px'
                  }}
                />

                <TextField
                  placeholder="Your email address"
                  fullWidth
                  value={email}
                  error={Boolean(emailError)}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (emailError) setEmailError("");
                    if (sendSuccess) setSendSuccess("");
                  }}
                  sx={{
                    maxWidth: { md: '100%' },
                    backgroundColor: "#E5E7EB",
                    borderRadius: 1,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1,
                      "& fieldset": { border: "none" },
                    },
                  }}
                />

                <Button
                  variant="contained"
                  onClick={handleSendTranscript}
                  disabled={sendingEmail}
                  sx={{
                    backgroundColor: "#D10000",
                    px: 6,
                    py: 1.6,
                    fontWeight: 400,
                    borderRadius: 1,
                    whiteSpace: "nowrap",
                    "&:hover": {
                      backgroundColor: "#b00000",
                    },
                    width: { xs: '100%', md: '300px' },
                  }}
                >
                  {sendingEmail ? <CircularProgress size={18} color="inherit" /> : 'SEND MY CONVERSATION »'}
                </Button>
              </Box>
              {(emailError || sendSuccess) && (
                <Typography
                  variant="body2"
                  sx={{
                    mt: 1,
                    color: emailError ? 'error.main' : 'success.main',
                    fontWeight: 500,
                  }}
                >
                  {emailError || sendSuccess}
                </Typography>
              )}
            </Container>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Email Input Section */}
      {/* <Box
        sx={{
          backgroundColor: "#fff",
          py: { xs: 6, md: 8 },
          my: '10px',
          px: { xs: 0, md: 3 },
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 30,
            left: { xs: 20, md: 120 },
            width: 60,
            opacity: 0.4,
          }}
        />

        <Container maxWidth="md">
          <Box textAlign="center" mb={3}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 500,
                color: "#374151",
                fontSize: { xs: "1rem", md: "1.2rem" },
              }}
            >
              When you're finished, add your email to receive a copy of the
              conversation.
            </Typography>
          </Box>

          <Box
            sx={{
              position: "relative", // important
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: { xs: '10px' , md: 0 },
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              component="img"
              src="/images/cross-copy.png" // dummy image
              alt="decorative pattern"
              sx={{
                position: "absolute",
                top: -44,
                left: { xs: '-23px', md: -57 },
                width: 79,
                opacity: 0.4,
                pointerEvents: "none",
                maxHeight: '130px'
              }}
            />

            <TextField
              placeholder="Your email address"
              fullWidth
              value={email}
              error={Boolean(emailError)}
              onChange={(e) => {
                setEmail(e.target.value);
                if (emailError) setEmailError("");
                if (sendSuccess) setSendSuccess("");
              }}
              sx={{
                maxWidth: { md: '100%' },
                backgroundColor: "#E5E7EB",
                borderRadius: 1,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1,
                  "& fieldset": { border: "none" },
                },
              }}
            />

            <Button
              variant="contained"
              onClick={handleSendTranscript}
              disabled={sendingEmail}
              sx={{
                backgroundColor: "#D10000",
                px: 4,
                py: 1.6,
                fontWeight: 400,
                borderRadius: 1,
                whiteSpace: "nowrap",
                "&:hover": {
                  backgroundColor: "#b00000",
                },
                width: { xs: '100%', md: '300px' },
              }}
            >
              {sendingEmail ? <CircularProgress size={18} color="inherit" /> : 'SEND MY CONVERSATION »'}
            </Button>
          </Box>
          {(emailError || sendSuccess) && (
            <Typography
              variant="body2"
              sx={{
                mt: 1,
                color: emailError ? 'error.main' : 'success.main',
                fontWeight: 500,
              }}
            >
              {emailError || sendSuccess}
            </Typography>
          )}
        </Container>
      </Box> */}

      {/* {!hideChrome && <Footer />} */}
    </>
  );
}

function ThreeDots({ size = 10, color = ACCENT_COLOR }) {
  const bounce = keyframes`
    0%, 80%, 100% { transform: translateY(0); opacity: 0.35; }
    40% { transform: translateY(-6px); opacity: 1; }
  `;

  const dotStyle = (delay) => ({
    width: size,
    height: size,
    backgroundColor: "#D10000",
    borderRadius: '50%',
    display: 'inline-block',
    animation: `${bounce} 1s infinite`,
    animationDelay: delay,
    animationFillMode: 'both',
  });

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
      <Box component="span" sx={dotStyle('0s')} />
      <Box component="span" sx={dotStyle('0.12s')} />
      <Box component="span" sx={dotStyle('0.24s')} />
    </Box>
  );
}

export default AskParis;

